import React, { MouseEvent } from 'react';
import { Icon } from '@iconify/react';
import { Seo } from '../../lib/seo';
import { PageHero } from '../../components/PageHero';
import './Module.scss';

interface TechItem {
  name: string;
  icon: string;
  url: string;
  color?: string;
}

interface TechSector {
  id: string;
  tag: string;
  title: string;
  description: string;
  icon: string;
  techs: TechItem[];
}

const techSectors: TechSector[] = [
  {
    id: 'networks',
    tag: 'INFRAESTRUTURA DE REDE',
    title: 'Redes, Roteamento & Provedores',
    description: 'Soluções de alta disponibilidade, roteamento avançado, switches gerenciáveis e arquitetura de DNS.',
    icon: 'lucide:network',
    techs: [
      { name: 'MikroTik RouterOS', icon: 'simple-icons:mikrotik', url: 'https://mikrotik.com/', color: '#1B1B1B' },
      { name: 'Intelbras Redes', icon: 'simple-icons:intel', url: 'https://www.intelbras.com/pt-br/redes', color: '#00A335' },
      { name: 'Cisco IOS', icon: 'simple-icons:cisco', url: 'https://www.cisco.com/', color: '#049fd9' },
      { name: 'Ubiquiti UniFi', icon: 'simple-icons:ubiquiti', url: 'https://ui.com/', color: '#0559C9' },
      { name: 'DNS & Anycast', icon: 'lucide:globe', url: 'https://www.cloudflare.com/learning/dns/what-is-dns/', color: '#3975ff' },
      { name: 'IPv6 / BGP Routing', icon: 'lucide:route', url: 'https://en.wikipedia.org/wiki/Border_Gateway_Protocol', color: '#22d3ee' },
    ],
  },
  {
    id: 'security',
    tag: 'SEGURANÇA ELETRÔNICA & CFTV',
    title: 'CFTV, Controle de Acesso & Alarmes',
    description: 'Sistemas inteligentes de videomonitoramento IP, gravação NVR, reconhecimento facial e alarmes corporativos.',
    icon: 'lucide:shield-check',
    techs: [
      { name: 'Intelbras CFTV IP', icon: 'lucide:video', url: 'https://www.intelbras.com/pt-br/seguranca-eletronica', color: '#00A335' },
      { name: 'Intelbras Acesso', icon: 'lucide:fingerprint', url: 'https://www.intelbras.com/pt-br/controle-de-acesso', color: '#00A335' },
      { name: 'Intelbras Alarmes', icon: 'lucide:bell-ring', url: 'https://www.intelbras.com/pt-br/alarmes', color: '#00A335' },
      { name: 'Hikvision', icon: 'simple-icons:hikvision', url: 'https://www.hikvision.com/', color: '#EE3124' },
      { name: 'NVR / VMS Systems', icon: 'lucide:hard-drive', url: 'https://en.wikipedia.org/wiki/Network_video_recorder', color: '#8b7cff' },
    ],
  },
  {
    id: 'telecom',
    tag: 'TELECOM & COMUNICAÇÃO',
    title: 'Telefonia IP, PABX & Comunicação Unificada',
    description: 'Centrais telefônicas digitais, servidores SIP, ramais remotos e integração de voz corporativa.',
    icon: 'lucide:phone-call',
    techs: [
      { name: 'Intelbras Telecom & PABX', icon: 'lucide:phone-forwarded', url: 'https://www.intelbras.com/pt-br/comunicacao', color: '#00A335' },
      { name: 'Asterisk PBX', icon: 'simple-icons:asterisk', url: 'https://www.asterisk.org/', color: '#F15A24' },
      { name: 'FreePBX', icon: 'lucide:server', url: 'https://www.freepbx.org/', color: '#35d39a' },
      { name: 'SIP Trunking', icon: 'lucide:radio', url: 'https://en.wikipedia.org/wiki/SIP_trunking', color: '#3975ff' },
      { name: 'Grandstream', icon: 'lucide:phone', url: 'https://www.grandstream.com/', color: '#006699' },
    ],
  },
  {
    id: 'cloud-devops',
    tag: 'CLOUD & INFRAESTRUTURA',
    title: 'Nuvem, Servidores & Virtualização',
    description: 'Serviços em nuvem, proteção de borda (Edge), bancos de dados distribuídos e containers isolados.',
    icon: 'lucide:cloud',
    techs: [
      { name: 'Cloudflare', icon: 'devicon:cloudflare', url: 'https://www.cloudflare.com/' },
      { name: 'Cloudflare D1', icon: 'devicon:sqlite', url: 'https://developers.cloudflare.com/d1/' },
      { name: 'Amazon AWS', icon: 'simple-icons:amazonaws', url: 'https://aws.amazon.com/', color: '#FF9900' },
      { name: 'Google Cloud', icon: 'devicon:googlecloud', url: 'https://cloud.google.com/' },
      { name: 'Linux OS', icon: 'devicon:linux', url: 'https://www.kernel.org/' },
      { name: 'Docker', icon: 'devicon:docker', url: 'https://www.docker.com/' },
      { name: 'Proxmox VE', icon: 'simple-icons:proxmox', url: 'https://www.proxmox.com/', color: '#E57000' },
      { name: 'TrueNAS / Storage', icon: 'simple-icons:truenas', url: 'https://www.truenas.com/', color: '#0095D5' },
    ],
  },
  {
    id: 'development',
    tag: 'DESENVOLVIMENTO FULL STACK',
    title: 'Software, APIs & Engenharia Web',
    description: 'Desenvolvimento moderno com tipagem estrita, performance em tempo real e arquitetura desacoplada.',
    icon: 'lucide:code-2',
    techs: [
      { name: 'React', icon: 'devicon:react', url: 'https://react.dev/' },
      { name: 'TypeScript', icon: 'devicon:typescript', url: 'https://www.typescriptlang.org/' },
      { name: 'Node.js', icon: 'devicon:nodejs', url: 'https://nodejs.org/' },
      { name: 'JavaScript ES6+', icon: 'devicon:javascript', url: 'https://developer.mozilla.org/pt-BR/docs/Web/JavaScript' },
      { name: 'Python', icon: 'devicon:python', url: 'https://www.python.org/' },
      { name: 'Vite', icon: 'devicon:vitejs', url: 'https://vite.dev/' },
      { name: 'PostgreSQL', icon: 'devicon:postgresql', url: 'https://www.postgresql.org/' },
      { name: 'REST API', icon: 'carbon:api', url: 'https://restfulapi.net/', color: '#00c9ff' },
      { name: 'Git Workflow', icon: 'devicon:git', url: 'https://git-scm.com/' },
    ],
  },
  {
    id: 'energy',
    tag: 'ENERGIA & ELÉTRICA',
    title: 'Energia Ininterrupta, Solar & Automação',
    description: 'No-breaks senoidais para racks, bancos de baterias, sistemas fotovoltaicos e proteção contra surtos.',
    icon: 'lucide:zap',
    techs: [
      { name: 'Intelbras Energia', icon: 'lucide:battery-charging', url: 'https://www.intelbras.com/pt-br/energia', color: '#00A335' },
      { name: 'Intelbras Solar', icon: 'lucide:sun', url: 'https://www.intelbras.com/pt-br/solar', color: '#00A335' },
      { name: 'Nobreaks Senoidais', icon: 'lucide:cpu', url: 'https://en.wikipedia.org/wiki/Uninterruptible_power_supply', color: '#22d3ee' },
      { name: 'DPS & Aterramento', icon: 'lucide:shield-alert', url: 'https://en.wikipedia.org/wiki/Surge_protector', color: '#35d39a' },
      { name: 'IoT / Smart Home', icon: 'lucide:home', url: 'https://en.wikipedia.org/wiki/Smart_home', color: '#8b7cff' },
    ],
  },
];

export function TechnologiesPage() {
  const handleMouseMove = (e: MouseEvent<HTMLAnchorElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
  };

  return (
    <>
      <Seo
        title="Tecnologias e Parceiros"
        description="Stack tecnológico, setores de infraestrutura, redes, segurança e parceiros homologados da A3GS Tec."
        path="/tecnologias"
      />

      <PageHero
        eyebrow="TECNOLOGIAS & PARCEIROS"
        title="O ecossistema completo para a sua operação."
        description="Infraestrutura robusta dividida por setores estratégicos: redes, segurança inteligente, telecomunicações, nuvem e desenvolvimento de software."
      />

      <section className="section tech-sectors-section">
        <div className="container tech-sectors-grid">
          {techSectors.map((sector) => (
            <article key={sector.id} className="sector-card">
              <header className="sector-header">
                <div className="sector-icon-box">
                  <Icon icon={sector.icon} width="24" height="24" />
                </div>
                <div className="sector-title-wrap">
                  <span className="section-tag">{sector.tag}</span>
                  <h2 className="sector-title">{sector.title}</h2>
                </div>
              </header>

              <p className="sector-description">{sector.description}</p>

              <div className="tech-cloud">
                {sector.techs.map((tech) => (
                  <a
                    key={tech.name}
                    href={tech.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tech-badge"
                    onMouseMove={handleMouseMove}
                  >
                    <Icon
                      icon={tech.icon}
                      width="20"
                      height="20"
                      className="tech-badge-icon"
                      style={tech.color ? { color: tech.color } : undefined}
                    />
                    <span className="tech-badge-name">{tech.name}</span>
                  </a>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}